"use client";

import { eventLinkRel } from "@/lib/seo-enrichment";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Bell } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { useAuth } from "../auth/auth-context";
import { Event } from "@/lib/types";

interface ListSidebarProps {
  cityName: string;
  events: Event[];
  citySlug: string;
}

export function ListSidebar({ cityName, events, citySlug }: ListSidebarProps) {
  const t = useTranslations("Sidebar");
  const {
    user,
    subscribedArtistIds,
    subscribedVenueIds,
    subscribedEventIds,
  } = useAuth();

  const hasAnySubscriptions =
    subscribedArtistIds.size > 0 ||
    subscribedVenueIds.size > 0 ||
    subscribedEventIds.size > 0;

  const matchingEvents = useMemo(() => {
    if (!user || !hasAnySubscriptions) return [];
    const seen = new Set<number>();
    return events
      .filter((e) => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return (
          subscribedEventIds.has(e.id) ||
          subscribedVenueIds.has(e.venue.id) ||
          e.attractions.some((a) => subscribedArtistIds.has(a.id))
        );
      })
      .sort((a, b) => {
        const da = a.local_date ?? "";
        const db = b.local_date ?? "";
        return da.localeCompare(db);
      })
      .slice(0, 5);
  }, [
    user,
    hasAnySubscriptions,
    events,
    subscribedEventIds,
    subscribedVenueIds,
    subscribedArtistIds,
  ]);

  // Signed-in: show personalized content
  if (user) {
    return (
      <div className="hidden lg:block w-[280px] shrink-0 lg:self-stretch">
        <div className="sticky top-8">
          <div className="overflow-hidden rounded-lg border border-neutral-800 bg-surface">
            {!hasAnySubscriptions ? (
              /* State 1: No subscriptions at all */
              <div className="px-5 py-6">
                <div className="flex flex-col items-center text-center">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-neutral-700 bg-neutral-800/80">
                    <Bell className="h-5 w-5 text-neutral-500" />
                  </div>
                  <h3 className="mt-3 font-mono text-xs font-bold tracking-[0.15em] text-neutral-300">
                    {t("noFollowsYet")}
                  </h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-neutral-500">
                    {t("noFollowsDesc")}
                  </p>
                </div>
              </div>
            ) : matchingEvents.length > 0 ? (
              /* State 2: Has subscriptions + matching events */
              <>
                <div className="px-5 pt-5 pb-3">
                  <p className="font-mono text-[10px] tracking-[0.2em] text-acid">
                    {t("yourUpcomingShows")}
                  </p>
                </div>
                <div className="px-5 pb-4 space-y-2.5">
                  {matchingEvents.map((event) => {
                    const dateStr = event.local_date;
                    let month = "";
                    let day = "";
                    if (dateStr) {
                      const d = new Date(dateStr + "T00:00:00");
                      month = d
                        .toLocaleDateString("en", { month: "short" })
                        .toUpperCase();
                      day = String(d.getDate());
                    }
                    return (
                      <Link
                        key={event.id}
                        href={`/${citySlug}/event/${event.slug}`}
                        rel={eventLinkRel(event)} prefetch={false}
                        className="flex gap-3 items-start group rounded-md p-1.5 -mx-1.5 transition-colors hover:bg-neutral-800/60"
                      >
                        <div className="flex flex-col items-center justify-center w-10 shrink-0 rounded bg-neutral-800/80 py-1">
                          <span className="font-mono text-[9px] tracking-wider text-neutral-500 leading-none">
                            {month || "TBA"}
                          </span>
                          <span className="font-mono text-sm font-bold text-white leading-tight">
                            {day || "?"}
                          </span>
                        </div>
                        <div className="min-w-0 pt-0.5">
                          <p className="font-mono text-[11px] font-bold text-neutral-200 leading-snug truncate group-hover:text-white">
                            {event.name}
                          </p>
                          <p className="text-[11px] text-neutral-500 truncate">
                            {event.venue.name}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            ) : (
              /* State 3: Has subscriptions but no matches in this city */
              <div className="px-5 py-6">
                <div className="flex flex-col items-center text-center">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-neutral-700 bg-neutral-800/80">
                    <Bell className="h-5 w-5 text-neutral-500" />
                  </div>
                  <p className="mt-3 text-[13px] leading-relaxed text-neutral-500">
                    {t("noMatchesInCity", { city: cityName })}
                  </p>
                </div>
              </div>
            )}

            {/* Footer: link to profile */}
            <div className="px-5 pb-5">
              <Link
                href="/profile"
                rel="nofollow"
                className="flex w-full items-center justify-center gap-2 rounded-md bg-neutral-800 px-4 py-2.5 font-mono text-[11px] font-bold tracking-wider text-neutral-300 transition-all hover:bg-neutral-700 hover:text-white"
              >
                {t("viewProfile")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Unsigned-in: existing CTA
  const features = [
    {
      label: t("bellArtists"),
      desc: t("bellArtistsDesc"),
    },
    {
      label: t("bellVenues"),
      desc: t("bellVenuesDesc"),
    },
    {
      label: t("bellShows"),
      desc: t("bellShowsDesc"),
    },
  ];

  return (
    <div className="hidden lg:block w-[280px] shrink-0 lg:self-stretch">
      <div className="sticky top-8">
        <div className="overflow-hidden rounded-lg border border-neutral-800 bg-surface">
          {/* Header */}
          <div className="relative px-5 pt-6 pb-5">
            {/* Glow effect behind bell */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 h-16 w-16 rounded-full bg-acid/10 blur-2xl" />

            <div className="relative flex flex-col items-center text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-acid/30 bg-acid/10">
                <Bell className="h-5 w-5 text-acid" />
              </div>
              <h3 className="mt-3 font-mono text-xs font-bold tracking-[0.15em] text-white">
                {t("stayInTheLoop")}
              </h3>
              <p className="mt-2 text-[13px] leading-relaxed text-neutral-400">
                {t("tagline", { city: cityName })}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="mx-5 border-t border-neutral-800" />

          {/* How it works */}
          <div className="px-5 pt-4 pb-5">
            <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-3">
              {t("howItWorks")}
            </p>

            <div className="space-y-3.5">
              {features.map((feature, i) => (
                <div key={i} className="flex gap-3">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-800/80">
                    <Bell className="h-3.5 w-3.5 text-neutral-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] font-bold text-neutral-300 leading-none mb-1">
                      {feature.label}
                    </p>
                    <p className="text-[12px] leading-snug text-neutral-500">
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="px-5 pb-5">
            <Link
              href="/auth/signup"
              rel="nofollow"
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-acid px-4 py-2.5 font-mono text-[11px] font-bold tracking-wider text-black transition-all hover:brightness-110 hover:shadow-[0_0_20px_rgba(209,248,5,0.25)]"
            >
              {t("signUpCta")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
